/**
 * Persistent quote request line items (localStorage).
 */
(function () {
  const STORAGE_KEY = "surge_quote_request_v1";
  const MAX_ITEMS = 20;
  const STORAGE_VERSION = 1;

  function normalizeVariantId(id) {
    if (!id) return "";
    return String(id).replace(/\D/g, "");
  }

  function normalizeLine(line) {
    const variantId = normalizeVariantId(line?.variantId);
    if (!variantId) return null;

    const quantity = parseInt(line?.quantity, 10);
    return {
      variantId,
      quantity: Number.isNaN(quantity) || quantity < 1 ? 1 : quantity,
      productTitle: String(line?.productTitle || "").trim(),
      productHandle: String(line?.productHandle || "").trim(),
      productUrl: String(line?.productUrl || "").trim(),
      productImageUrl: String(line?.productImageUrl || "").trim(),
      productSku: String(line?.productSku || "").trim(),
    };
  }

  function readRaw() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return { version: STORAGE_VERSION, items: [] };
      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.items)) return { version: STORAGE_VERSION, items: [] };
      return parsed;
    } catch {
      return { version: STORAGE_VERSION, items: [] };
    }
  }

  function writeRaw(data) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: STORAGE_VERSION,
          updatedAt: new Date().toISOString(),
          items: data.items,
        }),
      );
      dispatchUpdate(data.items.length);
      return true;
    } catch {
      return false;
    }
  }

  function dispatchUpdate(count) {
    document.dispatchEvent(
      new CustomEvent("quote-request:updated", {
        detail: { count },
      }),
    );
  }

  const QuoteRequestStorage = {
    getItems() {
      const data = readRaw();
      return data.items.map(normalizeLine).filter(Boolean);
    },

    setItems(items) {
      const normalized = (items || []).map(normalizeLine).filter(Boolean).slice(0, MAX_ITEMS);
      return writeRaw({ items: normalized });
    },

    addOrUpdateFromLine(line) {
      const incoming = normalizeLine(line);
      if (!incoming) return { ok: false, reason: "invalid_line" };

      const items = this.getItems();
      const index = items.findIndex((item) => item.variantId === incoming.variantId);

      if (index >= 0) {
        const existing = items[index];
        items[index] = {
          ...incoming,
          quantity: existing.quantity,
        };
      } else {
        if (items.length >= MAX_ITEMS) {
          return { ok: false, reason: "max_items" };
        }
        items.push(incoming);
      }

      const saved = this.setItems(items);
      return { ok: saved, items: this.getItems() };
    },

    updateQuantity(variantId, quantity) {
      const id = normalizeVariantId(variantId);
      const qty = parseInt(quantity, 10);
      if (!id || Number.isNaN(qty) || qty < 1) return false;

      const items = this.getItems();
      const index = items.findIndex((item) => item.variantId === id);
      if (index < 0) return false;

      items[index].quantity = qty;
      return this.setItems(items);
    },

    removeItem(variantId) {
      const id = normalizeVariantId(variantId);
      if (!id) return false;
      const items = this.getItems().filter((item) => item.variantId !== id);
      return this.setItems(items);
    },

    clear() {
      try {
        localStorage.removeItem(STORAGE_KEY);
        dispatchUpdate(0);
        return true;
      } catch {
        return false;
      }
    },

    getCount() {
      return this.getItems().length;
    },
  };

  window.QuoteRequestStorage = QuoteRequestStorage;
})();
